import { CareOSLoader } from '@/components/ui/careos-loader';
import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AppLayout } from '@/components/layout/AppLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/contexts/AuthContext';
import { useFocusedPatient } from '@/hooks/useFocusedPatient';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { SignedAvatar } from '@/components/ui/signed-avatar';
import { SignedImage } from '@/components/ui/signed-image';
import { Separator } from '@/components/ui/separator';
import {
  Bell,
  Check,
  CheckCheck,
  Trash2,
  Loader2,
  AlertTriangle,
  Info,
  AlertCircle,
  Calendar,
  User,
  ClipboardList,
  Activity,
  ExternalLink,
  CreditCard
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';

interface NotificationData {
  id: string;
  user_id: string;
  patient_id: string;
  type: string;
  title: string;
  message: string;
  task_execution_id: string | null;
  comment_id: string | null;
  is_read: boolean;
  created_at: string;
}

interface TaskExecutionData {
  id: string;
  executed_at: string;
  is_alert: boolean;
  notes: string | null;
  vital_value: number | null;
  vital_value_secondary: number | null;
  food_acceptance: string | null;
  photo_url: string | null;
  task: {
    name: string;
    task_type: string;
    vital_type: string | null;
    unit: string | null;
  } | null;
  executor: {
    full_name: string | null;
    avatar_url: string | null;
  } | null;
  patient: {
    nickname: string;
  } | null;
}

export default function NotificationDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { setFocusedPatientId } = useFocusedPatient();

  const [notification, setNotification] = useState<NotificationData | null>(null);
  const [taskExecution, setTaskExecution] = useState<TaskExecutionData | null>(null);
  const [userRoleForPatient, setUserRoleForPatient] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    if (id) {
      fetchNotificationDetails();
    }
  }, [id]);

  const fetchNotificationDetails = async () => {
    try {
      // Fetch notification
      const { data: notifData, error: notifError } = await supabase
        .from('notifications')
        .select('*')
        .eq('id', id)
        .single();

      if (notifError) throw notifError;

      setNotification(notifData);

      // Mark as read if not already
      if (!notifData.is_read) {
        await supabase
          .from('notifications')
          .update({ is_read: true })
          .eq('id', id);

        setNotification(prev => prev ? { ...prev, is_read: true } : null);
      }

      // Fetch user's role for this patient
      if (notifData.patient_id && user) {
        const { data: roleData } = await supabase
          .from('patient_user_links')
          .select('role')
          .eq('patient_id', notifData.patient_id)
          .eq('user_id', user.id)
          .maybeSingle();

        setUserRoleForPatient(roleData?.role || null);
      }

      // Fetch task execution details if available
      if (notifData.task_execution_id) {
        const { data: execData } = await supabase
          .from('task_executions')
          .select('id, executed_at, is_alert, notes, vital_value, vital_value_secondary, food_acceptance, photo_url, task_id, executed_by, patient_id')
          .eq('id', notifData.task_execution_id)
          .single();

        if (execData) {
          // Fetch related data
          const [taskResult, profileResult, patientResult] = await Promise.all([
            supabase
              .from('care_plan_tasks')
              .select('name, task_type, vital_type, unit')
              .eq('id', execData.task_id)
              .single(),
            supabase
              .from('profiles')
              .select('full_name, avatar_url')
              .eq('user_id', execData.executed_by)
              .single(),
            supabase
              .from('patients')
              .select('nickname')
              .eq('id', execData.patient_id)
              .single()
          ]);

          setTaskExecution({
            ...execData,
            task: taskResult.data,
            executor: profileResult.data,
            patient: patientResult.data
          });
        }
      }
    } catch (error) {
      console.error('Error fetching notification:', error);
      toast.error('Erro ao carregar notificação');
    } finally {
      setIsLoading(false);
    }
  };

  const handleMarkAsRead = async () => {
    if (!notification || notification.is_read) return;

    try {
      const { error } = await supabase
        .from('notifications')
        .update({ is_read: true })
        .eq('id', notification.id);

      if (error) throw error;

      setNotification(prev => prev ? { ...prev, is_read: true } : null);
      toast.success('Marcada como lida');
    } catch (error) {
      console.error('Error marking as read:', error);
      toast.error('Erro ao marcar como lida');
    }
  };

  const handleDelete = async () => {
    if (!notification) return;

    setIsDeleting(true);
    try {
      const { error } = await supabase
        .from('notifications')
        .delete()
        .eq('id', notification.id);

      if (error) throw error;

      toast.success('Notificação removida');
      navigate('/app/notifications');
    } catch (error) {
      console.error('Error deleting notification:', error);
      toast.error('Erro ao remover notificação');
    } finally {
      setIsDeleting(false);
    }
  };

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'alert':
      case 'stock_alert':
      case 'low_stock':
        return <AlertTriangle className="w-6 h-6" />;
      case 'critical':
      case 'vital_alert':
        return <AlertCircle className="w-6 h-6" />;
      case 'task_execution':
      case 'task_registered':
        return <CheckCheck className="w-6 h-6" />;
      case 'task_comment':
      case 'comment_reply':
      case 'mention':
        return <Bell className="w-6 h-6" />;
      case 'incident':
        return <AlertTriangle className="w-6 h-6 text-destructive" />;
      case 'billing_payment_failed':
      case 'billing_grace_expired':
      case 'billing_recovered':
        return <CreditCard className="w-6 h-6" />;
      default:
        return <Info className="w-6 h-6" />;
    }
  };

  const getNotificationColor = (type: string) => {
    switch (type) {
      case 'alert':
      case 'stock_alert':
      case 'low_stock':
      case 'billing_payment_failed':
        return 'text-warning bg-warning/10';
      case 'critical':
      case 'billing_grace_expired':
        return 'text-destructive bg-destructive/10';
      case 'billing_recovered':
        return 'text-success bg-success/10';
      default:
        return 'text-primary bg-primary/10';
    }
  };

  const getTaskTypeName = (type: string) => {
    const types: Record<string, string> = {
      fixed_medication: 'Medicamento Fixo',
      sporadic_medication: 'Medicamento Esporádico',
      vital_sign: 'Sinal Vital',
      hygiene: 'Higiene',
      food: 'Alimentação',
      physical_activity: 'Atividade Física',
      incident: 'Incidente',
      other: 'Outra Tarefa'
    };
    return types[type] || type;
  };

  if (isLoading) {
    return (
      <AppLayout title="Detalhes da notificação" showBack showFAB={false}>
        <div className="flex items-center justify-center h-64">
          <CareOSLoader size="lg" />
        </div>
      </AppLayout>
    );
  }

  if (!notification) {
    return (
      <AppLayout title="Detalhes" showBack showFAB={false}>
        <div className="p-4">
          <Card>
            <CardContent className="p-8 text-center text-muted-foreground">
              <Bell className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>Notificação não encontrada</p>
            </CardContent>
          </Card>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout
      title="Detalhes da notificação"
      showBack
      onBack={() => navigate('/app/notifications')}
      showFAB={false}
    >
      <div className="p-4 space-y-4 pb-24">
        {/* Notification Header */}
        <Card>
          <CardContent className="p-4">
            <div className="flex items-start gap-4">
              <div className={cn(
                "p-3 rounded-full shrink-0",
                getNotificationColor(notification.type)
              )}>
                {getNotificationIcon(notification.type)}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-2">
                  <h2 className="font-semibold text-lg">
                    {notification.title}
                  </h2>
                  {!notification.is_read && (
                    <Badge variant="default">Nova</Badge>
                  )}
                </div>
                <p className="text-muted-foreground">
                  {notification.message}
                </p>
                <div className="flex items-center gap-2 mt-3 text-sm text-muted-foreground">
                  <Calendar className="w-4 h-4" />
                  {format(new Date(notification.created_at), "EEEE, d 'de' MMMM 'de' yyyy 'às' HH:mm", { locale: ptBR })}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Task Execution Details */}
        {taskExecution && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <ClipboardList className="w-5 h-5 text-primary" />
                Detalhes do Evento
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Task Info */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Tarefa</span>
                  <span className="font-medium">{taskExecution.task?.name}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Tipo</span>
                  <Badge variant="outline">
                    {getTaskTypeName(taskExecution.task?.task_type || '')}
                  </Badge>
                </div>
                {taskExecution.patient && (
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Paciente</span>
                    <span className="font-medium">{taskExecution.patient.nickname}</span>
                  </div>
                )}
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Data/Hora</span>
                  <span>{format(new Date(taskExecution.executed_at), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}</span>
                </div>
                {taskExecution.is_alert && (
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Status</span>
                    <Badge variant="destructive" className="flex items-center gap-1">
                      <AlertTriangle className="w-3 h-3" />
                      Alerta
                    </Badge>
                  </div>
                )}
              </div>

              {/* Vital Values */}
              {taskExecution.vital_value !== null && (
                <>
                  <Separator />
                  <div className="space-y-2">
                    <h4 className="font-medium flex items-center gap-2">
                      <Activity className="w-4 h-4 text-primary" />
                      Valores Registrados
                    </h4>
                    <div className="p-3 bg-muted/50 rounded-lg">
                      {taskExecution.task?.vital_type === 'blood_pressure' ? (
                        <p className="text-lg font-semibold">
                          {taskExecution.vital_value}/{taskExecution.vital_value_secondary} mmHg
                        </p>
                      ) : (
                        <p className="text-lg font-semibold">
                          {taskExecution.vital_value} {taskExecution.task?.unit || ''}
                        </p>
                      )}
                    </div>
                  </div>
                </>
              )}

              {/* Food Acceptance */}
              {taskExecution.food_acceptance && (
                <>
                  <Separator />
                  <div className="space-y-2">
                    <h4 className="font-medium">Aceitação</h4>
                    <Badge variant="outline">{taskExecution.food_acceptance}</Badge>
                  </div>
                </>
              )}

              {/* Notes */}
              {taskExecution.notes && (
                <>
                  <Separator />
                  <div className="space-y-2">
                    <h4 className="font-medium">Observações</h4>
                    <p className="text-sm text-muted-foreground p-3 bg-muted/50 rounded-lg">
                      {taskExecution.notes}
                    </p>
                  </div>
                </>
              )}

              {/* Photo */}
              {taskExecution.photo_url && (
                <>
                  <Separator />
                  <div className="space-y-2">
                    <h4 className="font-medium">Foto</h4>
                    <SignedImage
                      src={taskExecution.photo_url}
                      bucket="shift-photos"
                      alt="Foto do registro"
                      className="w-full rounded-lg"
                    />
                  </div>
                </>
              )}

              {/* Executor */}
              {taskExecution.executor && (
                <>
                  <Separator />
                  <div className="space-y-2">
                    <h4 className="font-medium flex items-center gap-2">
                      <User className="w-4 h-4 text-primary" />
                      Registrado por
                    </h4>
                    <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
                      <SignedAvatar
                        src={taskExecution.executor.avatar_url}
                        bucket="avatars"
                        className="w-10 h-10"
                        fallback={
                          <span className="bg-primary/10 text-primary flex items-center justify-center w-full h-full">
                            {taskExecution.executor.full_name?.charAt(0) || 'U'}
                          </span>
                        }
                      />
                      <span className="font-medium">
                        {taskExecution.executor.full_name || 'Usuário'}
                      </span>
                    </div>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        )}

        {/* View in Feed Button */}
        {(notification.patient_id || notification.type?.startsWith('dsr_') || notification.type?.startsWith('billing_')) && (
          <Button
            className="w-full"
            onClick={async () => {
              // DSR notifications → Privacy Center (user's own area)
              if (notification.type === 'dsr_updates' || notification.type === 'dsr_received') {
                navigate('/app/privacy');
                return;
              }

              // Billing notifications → Subscription page
              if (
                notification.type === 'billing_payment_failed' ||
                notification.type === 'billing_grace_expired' ||
                notification.type === 'billing_recovered'
              ) {
                navigate('/app/subscription');
                return;
              }

              const isCommentNotification = notification.type === 'task_comment' || notification.type === 'mention' || notification.type === 'comment_reply';
              const params = new URLSearchParams();

              if (taskExecution) {
                params.set('highlight', taskExecution.id);
              }

              // For comment notifications, also pass openComments and commentId to highlight specific comment
              if (isCommentNotification && taskExecution) {
                params.set('openComments', 'true');
                if (notification.comment_id) {
                  params.set('commentId', notification.comment_id);
                }
              }

              // Set focused patient before navigating to ensure menu consistency
              await setFocusedPatientId(notification.patient_id);

              // Navigate to correct route based on user role for this patient
              const isCaregiver = userRoleForPatient === 'caregiver';
              const basePath = isCaregiver
                ? `/app/caregiver/${notification.patient_id}`
                : `/app/family/${notification.patient_id}`;

              // Handle stock alerts redirection to inventory
              if (notification.type === 'stock_alert' || notification.type === 'low_stock') {
                navigate(`/app/inventory/${notification.patient_id}`);
                return;
              }

              const url = `${basePath}${params.toString() ? '?' + params.toString() : ''}`;
              navigate(url);
            }}
          >
            <ExternalLink className="w-4 h-4 mr-2" />
            Ver detalhes
          </Button>
        )}

        {/* Action Buttons */}
        <div className="flex gap-3">
          {!notification.is_read && (
            <Button
              variant="outline"
              className="flex-1"
              onClick={handleMarkAsRead}
            >
              <Check className="w-4 h-4 mr-2" />
              Marcar como lida
            </Button>
          )}
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button
                variant="outline"
                className="flex-1 text-destructive hover:text-destructive"
                disabled={isDeleting}
              >
                {isDeleting ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Trash2 className="w-4 h-4 mr-2" />
                )}
                Remover
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Remover notificação?</AlertDialogTitle>
                <AlertDialogDescription>
                  Esta ação não pode ser desfeita.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancelar</AlertDialogCancel>
                <AlertDialogAction onClick={handleDelete}>
                  Remover
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>
    </AppLayout>
  );
}
